/**
 * QuickCompsView - NVIDIA Quick Comparable Analysis page view
 *
 * Displays quick comparable company analysis with multiple tabs for different data views
 */

import { BaseView } from './BaseView';
import { DownloadService } from '../services/DownloadService';
import { DataService } from '../services/DataService';
import { OptionsDropdown, type ActionCallback, type DropdownAction } from '../components/OptionsDropdown';
import { CompanyDeletionHandler, SummaryCalculator } from '../components/FinancialTables';
import type {
	MonthValue,
	YearValue,
	BusinessDescriptionData,
	HeadquartersData,
	TradingMultiplesDataset,
	OperatingStatsDataset,
	FinancialDataset,
	DataYear
} from '../types';

export class QuickCompsView extends BaseView {
	private downloadService: DownloadService;
	private dataService: DataService;
	private businessDescriptions: BusinessDescriptionData | null = null;
	private headquarters: HeadquartersData | null = null;
	private financialData: FinancialDataset | null = null;
	private tradingMultiples: TradingMultiplesDataset | null = null;
	private operatingStats: OperatingStatsDataset | null = null;
	private currentDataYear: DataYear = '2025'; // Default to 2025
	private optionsDropdown: OptionsDropdown | null = null;
	private companyDeletionHandler: CompanyDeletionHandler | null = null;
	private summaryCalculator: SummaryCalculator | null = null;

	constructor() {
		super();
		this.downloadService = new DownloadService();
		this.dataService = new DataService();
		this.summaryCalculator = new SummaryCalculator();
		this.companyDeletionHandler = new CompanyDeletionHandler(this.summaryCalculator);
	}

	/**
	 * Render the quick comps view content
	 */
	render(): string {
		return `
			<style>
				@media print {
					.header,
					.navbar,
					.sidebar,
					.button-row {
						display: none !important;
					}

					@page {
						size: letter;
						margin: 0.5in;
					}

					body {
						margin: 0;
						padding: 0;
					}

					.content-container {
						display: block;
					}

					.main-content {
						width: 100% !important;
						margin: 0 !important;
					}
				}
			</style>

			<div class="company-header">
				<div class="company-name"><a href="/profile">NVIDIA Corporation (NasdaqGS:NVDA)</a> Quick Comparable Analysis</div>
			</div>

			<div class="button-row">
				<div class="tool-button download-button">
					<img src="../img/icon1.png" alt="Download Financials" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Download Financials</span>
				</div>
				<div class="tool-button print-button">
					<i data-lucide="printer" style="width: 16px; height: 16px; margin-right: 3px;"></i>
					<span class="button-text">Print</span>
				</div>
			</div>

			<div class="customize-view">
				<div class="customize-header">
					<span>Customize View</span>
				</div>
				<div class="customize-controls">
					<div class="control-group">
						<label>Comp Set:</label>
						<select>
							<option>NVIDIA Corporation Quick Comp</option>
						</select>
					</div>
					<div class="control-group">
						<label>Templates:</label>
						<select>
							<option>Capital IQ Default Comps</option>
						</select>
					</div>
					<button class="customize-go-button">Go</button>
				</div>
			</div>

			<div class="tabs-container">
				<div class="tab-group">
					<div class="tab active" data-tab="financial">Financial Data</div>
					<div class="tab" data-tab="trading">Trading Multiples</div>
					<div class="tab" data-tab="operating">Operating Statistics</div>
					<div class="tab" data-tab="business">Business Description</div>
				</div>
			</div>

			<div id="financial-tab" class="tab-content active">
				<div class="options-row">
					<div class="options-dropdown-container">
						<button class="options-button">Options ▼</button>
						<div class="options-dropdown">
							<div class="option-item">Chart Selected Companies</div>
							<div class="option-item">Delete</div>
						</div>
					</div>
					<div class="currency-selector">
						<label>Currency:</label>
						<select>
							<option>US Dollar</option>
						</select>
					</div>
					<div class="date-selector">
						<label>Data as of:</label>
						<select class="month-select">
							<option value="01">January</option>
							<option value="02">February</option>
							<option value="03" selected>March</option>
							<option value="04">April</option>
							<option value="05">May</option>
							<option value="06">June</option>
							<option value="07">July</option>
							<option value="08">August</option>
							<option value="09">September</option>
							<option value="10">October</option>
							<option value="11">November</option>
							<option value="12">December</option>
						</select>
						<select class="year-select">
							<option value="2023">2023</option>
							<option value="2024">2024</option>
							<option value="2025" selected>2025</option>
						</select>
						<button class="date-go-button">Go</button>
					</div>
				</div>
				<div class="comps-table-container">
					<table class="comps-table">
						<thead>
							<tr>
								<th></th>
								<th>Company Name</th>
								<th>Day Close Price Latest</th>
								<th>Shares Outstanding Latest</th>
								<th>Market Capitalization Latest</th>
								<th>LTM Net Debt</th>
								<th>LTM Total Pref. Equity</th>
								<th>LTM Minority Interest</th>
								<th>Total Enterprise Value Latest</th>
								<th>LTM Tangible Book Value/Share</th>
								<th>LTM Filing Date, Income Statement</th>
								<th>LTM Total Revenue</th>
								<th>LTM EBITDA</th>
								<th>LTM EBIT</th>
								<th>LTM Diluted EPS Excl. Extra Items</th>
								<th>NTM Revenue (ZapIQ)</th>
								<th>NTM EBITDA (ZapIQ)</th>
								<th>NTM EPS (ZapIQ)</th>
							</tr>
						</thead>
						<tbody>
							<!-- Rows will be dynamically populated from financial data JSON -->
						</tbody>
					</table>
				</div>

				<div class="summary-stats">
					<table class="summary-table">
						<!-- Summary statistics will be dynamically populated from financial data JSON -->
					</table>
				</div>
			</div>

			<div id="trading-tab" class="tab-content">
				<div class="options-row">
					<div class="options-dropdown-container">
						<button class="options-button">Options ▼</button>
						<div class="options-dropdown">
							<div class="option-item">Chart Selected Companies</div>
							<div class="option-item">Delete</div>
						</div>
					</div>
					<div class="currency-selector">
						<label>Currency:</label>
						<select>
							<option>US Dollar</option>
						</select>
					</div>
					<div class="date-selector">
						<label>Data as of:</label>
						<select class="month-select">
							<option value="01">January</option>
							<option value="02">February</option>
							<option value="03" selected>March</option>
							<option value="04">April</option>
							<option value="05">May</option>
							<option value="06">June</option>
							<option value="07">July</option>
							<option value="08">August</option>
							<option value="09">September</option>
							<option value="10">October</option>
							<option value="11">November</option>
							<option value="12">December</option>
						</select>
						<select class="year-select">
							<option value="2023">2023</option>
							<option value="2024">2024</option>
							<option value="2025" selected>2025</option>
						</select>
						<button class="date-go-button">Go</button>
					</div>
				</div>

				<div class="comps-table-container">
					<table class="comps-table trading-multiples">
						<thead>
							<tr>
								<th></th>
								<th>Company Name</th>
								<th>TEV/Total Revenues LTM - Latest</th>
								<th>TEV/EBITDA LTM - Latest</th>
								<th>TEV/EBIT LTM - Latest</th>
								<th>P/Diluted EPS Before Extra LTM - Latest</th>
								<th>P/TangBV LTM - Latest</th>
								<th>NTM TEV/Forward Total Revenue (Capital IQ)</th>
								<th>NTM TEV/Forward EBITDA (Capital IQ)</th>
								<th>NTM Forward P/E (Capital IQ)</th>
							</tr>
						</thead>
						<tbody>
							<!-- Rows will be dynamically populated from trading multiples JSON -->
						</tbody>
					</table>

					<table class="summary-table">
						<!-- Summary statistics will be dynamically populated from trading multiples JSON -->
					</table>
				</div>
			</div>

			<div id="operating-tab" class="tab-content">
				<div class="options-row">
					<div class="options-dropdown-container">
						<button class="options-button">Options ▼</button>
						<div class="options-dropdown">
							<div class="option-item">Chart Selected Companies</div>
							<div class="option-item">Delete</div>
						</div>
					</div>
					<div class="currency-selector">
						<label>Currency:</label>
						<select>
							<option>US Dollar</option>
						</select>
					</div>
					<div class="date-selector">
						<label>Data as of:</label>
						<select class="month-select">
							<option value="01">January</option>
							<option value="02">February</option>
							<option value="03" selected>March</option>
							<option value="04">April</option>
							<option value="05">May</option>
							<option value="06">June</option>
							<option value="07">July</option>
							<option value="08">August</option>
							<option value="09">September</option>
							<option value="10">October</option>
							<option value="11">November</option>
							<option value="12">December</option>
						</select>
						<select class="year-select">
							<option value="2023">2023</option>
							<option value="2024">2024</option>
							<option value="2025" selected>2025</option>
						</select>
						<button class="date-go-button">Go</button>
					</div>
				</div>

				<div class="comps-table-container">
					<table class="comps-table operating-stats">
						<thead>
							<tr>
								<th></th>
								<th>Company Name</th>
								<th>LTM Gross Margin %</th>
								<th>LTM EBITDA Margin %</th>
								<th>LTM EBIT Margin %</th>
								<th>LTM Net Income Margin %</th>
								<th>LTM Total Revenues, 1 Yr Growth %</th>
								<th>LTM EBITDA, 1 Yr Growth %</th>
								<th>LTM EBIT, 1 Yr Growth %</th>
								<th>LTM Net Income, 1 Yr Growth %</th>
								<th>LTM Total Debt/Capital %</th>
								<th>LTM Total Debt/EBITDA</th>
								<th>NTM LT EPS Growth Rate (Capital IQ)</th>
								<th>5 Year Beta</th>
							</tr>
						</thead>
						<tbody>
							<!-- Rows will be dynamically populated from operating stats JSON -->
						</tbody>
					</table>

					<table class="summary-table">
						<!-- Summary statistics will be dynamically populated from operating stats JSON -->
					</table>
				</div>
			</div>

			<div id="business-tab" class="tab-content">
				<div class="options-row">
					<div class="options-dropdown-container">
						<button class="options-button">Options ▼</button>
						<div class="options-dropdown">
							<div class="option-item">Chart Selected Companies</div>
							<div class="option-item">Delete</div>
						</div>
					</div>
					<div class="currency-selector">
						<label>Currency:</label>
						<select>
							<option>US Dollar</option>
						</select>
					</div>
					<div class="date-selector">
						<label>Data as of:</label>
						<select class="month-select">
							<option value="01">January</option>
							<option value="02">February</option>
							<option value="03" selected>March</option>
							<option value="04">April</option>
							<option value="05">May</option>
							<option value="06">June</option>
							<option value="07">July</option>
							<option value="08">August</option>
							<option value="09">September</option>
							<option value="10">October</option>
							<option value="11">November</option>
							<option value="12">December</option>
						</select>
						<select class="year-select">
							<option value="2023">2023</option>
							<option value="2024">2024</option>
							<option value="2025" selected>2025</option>
						</select>
						<button class="date-go-button">Go</button>
					</div>
				</div>
				<div class="comps-table-container">
					<table class="comps-table">
						<thead>
							<tr>
								<th></th>
								<th>Company Name</th>
								<th>Business Description</th>
								<th>Headquarters</th>
							</tr>
						</thead>
						<tbody>
							<!-- Rows will be dynamically populated from businessDescriptions.json and headquarters.json -->
						</tbody>
					</table>
					<div class="company-count" style="font-size:11px; padding:4px 0;">Displaying 0 Companies.</div>
				</div>
			</div>
		`;
	}

	/**
	 * Initialize tab switching and download functionality when view is mounted
	 */
	async onMount(): Promise<void> {
		this.initializeTabSwitching();
		this.initializeDownloadButton();
		await this.loadAllData();
		this.renderFinancialDataTable();
		this.renderTradingMultiplesTable();
		this.renderOperatingStatsTable();
		this.renderBusinessTable();
		this.initializeBusinessTabToggles();
		this.initializeDateSelectors();
		this.initializeOptionsDropdown();
	}

	/**
	 * Load all data (business descriptions, headquarters, financial data, trading multiples, operating stats)
	 */
	private async loadAllData(): Promise<void> {
		try {
			// Load business descriptions and headquarters (year-independent)
			[this.businessDescriptions, this.headquarters] = await Promise.all([
				this.dataService.loadBusinessDescriptions(),
				this.dataService.loadHeadquarters()
			]);

			// Try to load year-dependent data, fall back to 2024 if 2025 doesn't exist
			const yearToLoad = this.currentDataYear;
			try {
				[this.financialData, this.tradingMultiples, this.operatingStats] = await Promise.all([
					this.dataService.loadFinancialData(yearToLoad),
					this.dataService.loadTradingMultiples(yearToLoad),
					this.dataService.loadOperatingStats(yearToLoad)
				]);
			} catch (error) {
				if (yearToLoad === '2025') {
					console.warn('[QuickCompsView] 2025 data not available, falling back to 2024');
					[this.financialData, this.tradingMultiples, this.operatingStats] = await Promise.all([
						this.dataService.loadFinancialData('2024'),
						this.dataService.loadTradingMultiples('2024'),
						this.dataService.loadOperatingStats('2024')
					]);
				} else {
					throw error;
				}
			}
		} catch (error) {
			console.error('[QuickCompsView] Failed to load data:', error);
		}
	}

	/**
	 * Set up event listeners for tab switching
	 */
	private initializeTabSwitching(): void {
		const tabs = this.container?.querySelectorAll('.tab');

		if (!tabs || tabs.length === 0) {
			return;
		}

		tabs.forEach(tab => {
			this.addEventListener(tab as HTMLElement, 'click', () => {
				const tabName = tab.getAttribute('data-tab');
				if (tabName) {
					this.switchTab(tabName);
				}
			});
		});
	}

	/**
	 * Switch to a specific tab
	 * @param tabName - The name of the tab to switch to (financial, trading, operating, business)
	 */
	private switchTab(tabName: string): void {
		// Deactivate all tabs
		const tabs = this.container?.querySelectorAll('.tab');
		tabs?.forEach(tab => tab.classList.remove('active'));

		// Deactivate all tab contents
		const tabContents = this.container?.querySelectorAll('.tab-content');
		tabContents?.forEach(content => content.classList.remove('active'));

		// Activate the clicked tab
		const activeTab = this.container?.querySelector(`.tab[data-tab="${tabName}"]`);
		activeTab?.classList.add('active');

		// Activate the corresponding tab content
		const activeContent = this.container?.querySelector(`#${tabName}-tab`);
		activeContent?.classList.add('active');
	}

	/**
	 * Set up event listener for download button
	 */
	private initializeDownloadButton(): void {
		const downloadButton = this.container?.querySelector('.download-button');

		if (!downloadButton) {
			console.warn('[QuickCompsView] Download button not found in DOM');
			return;
		}


		this.addEventListener(downloadButton as HTMLElement, 'click', () => {
			this.handleDownloadClick();
		});

		// Set up Print button handler
		const printBtn = this.container?.querySelector('.print-button');
		if (printBtn) {
			this.addEventListener(printBtn as HTMLElement, 'click', () => {
				window.print();
			});
		}
	}

	/**
	 * Handle download button click
	 * Gets current date selection and visible companies, then triggers download
	 */
	private handleDownloadClick(): void {

		// Get current date selection
		const monthSelect = this.container?.querySelector('.month-select') as HTMLSelectElement | null;
		const yearSelect = this.container?.querySelector('.year-select') as HTMLSelectElement | null;

		if (!monthSelect || !yearSelect) {
			console.warn('[QuickCompsView] Date selectors not found');
			return;
		}

		const month = monthSelect.value as MonthValue;
		const year = yearSelect.value as YearValue;

		// Get visible companies from currently active tab
		const activeTab = this.container?.querySelector('.tab-content.active');
		const companyRows = activeTab?.querySelectorAll('.comps-table tbody tr');


		if (!companyRows || companyRows.length === 0) {
			console.warn('[QuickCompsView] No company rows found');
			return;
		}

		// Extract company names from visible rows
		const visibleCompanies = Array.from(companyRows)
			.map(row => {
				const companyNameCell = row.querySelector('td:nth-child(2)');
				return companyNameCell?.textContent?.trim() || '';
			})
			.filter(name => name.length > 0)
			.sort();


		// Trigger download using DownloadService
		this.downloadService.downloadFile({
			month,
			year,
			visibleCompanies
		});
	}

	/**
	 * Render the business description table with data from JSON files
	 */
	private renderBusinessTable(): void {
		if (!this.businessDescriptions || !this.headquarters) {
			console.warn('[QuickCompsView] Business data not loaded yet');
			return;
		}

		const tbody = this.container?.querySelector('#business-tab .comps-table tbody');
		if (!tbody) {
			console.warn('[QuickCompsView] Business table tbody not found');
			return;
		}

		// Define the company list in the desired order
		const companies = [
			'Intel Corporation (NasdaqGS:INTC)',
			'Advanced Micro Devices, Inc. (NasdaqGS:AMD)',
			'Broadcom Inc. (NasdaqGS:AVGO)',
			'QUALCOMM Incorporated (NasdaqGS:QCOM)',
			'Micron Technology, Inc. (NasdaqGS:MU)',
			'Texas Instruments Incorporated (NasdaqGS:TXN)',
			'Marvell Technology, Inc. (NasdaqGS:MRVL)',
			'Microsoft Corporation (NasdaqGS:MSFT)',
			'Taiwan Semiconductor Manufacturing Company Limited (TWSE:2330)',
			'Alphabet Inc. (NasdaqGS:GOOGL)',
			'NVIDIA Corporation (NasdaqGS:NVDA)'
		];

		// Clear existing rows
		tbody.innerHTML = '';

		// Generate rows
		let renderedCount = 0;
		companies.forEach(companyName => {
			const description = this.businessDescriptions?.[companyName];
			const hq = this.headquarters?.[companyName];

			if (!description || !hq) {
				console.warn(`[QuickCompsView] Missing data for ${companyName}`);
				return;
			}

			const row = document.createElement('tr');
			row.innerHTML = `
				<td></td>
				<td>${companyName}</td>
				<td>
					<span class="text-content" data-short="${this.escapeHtml(description.short)}" data-full="${this.escapeHtml(description.full)}">${description.short}</span>
					<span class="blue-link toggle-link" data-expanded="false"> More &gt;&gt;</span>
				</td>
				<td>
					<span class="text-content" data-short="${this.escapeHtml(hq.short)}" data-full="${this.escapeHtml(hq.full)}">${hq.short}</span>
					<span class="blue-link toggle-link" data-expanded="false"> More &gt;&gt;</span>
				</td>
			`;
			tbody.appendChild(row);
			renderedCount++;
		});

		// Update company count display
		const countDisplay = this.container?.querySelector('#business-tab .company-count');
		if (countDisplay) {
			countDisplay.textContent = `Displaying ${renderedCount} Companies.`;
		}
	}

	/**
	 * Initialize toggle functionality for More/Less links in business tab
	 */
	private initializeBusinessTabToggles(): void {
		const businessTab = this.container?.querySelector('#business-tab');
		if (!businessTab) {
			return;
		}

		// Use event delegation for all toggle links
		this.addEventListener(businessTab as HTMLElement, 'click', (event) => {
			const target = event.target as HTMLElement;

			if (target.classList.contains('toggle-link')) {
				event.preventDefault();

				const isExpanded = target.getAttribute('data-expanded') === 'true';
				const textContent = target.previousElementSibling as HTMLElement;

				if (!textContent) {
					return;
				}

				const shortText = textContent.getAttribute('data-short') || '';
				const fullText = textContent.getAttribute('data-full') || '';

				if (isExpanded) {
					// Collapse: show short text
					textContent.textContent = shortText;
					target.textContent = ' More >>';
					target.setAttribute('data-expanded', 'false');
				} else {
					// Expand: show full text
					textContent.textContent = fullText;
					target.textContent = ' Less <<';
					target.setAttribute('data-expanded', 'true');
				}
			}
		});
	}

	/**
	 * Render the financial data table with data from JSON files
	 */
	private renderFinancialDataTable(): void {
		if (!this.financialData) {
			console.warn('[QuickCompsView] Financial data not loaded yet');
			return;
		}

		const tbody = this.container?.querySelector('#financial-tab .comps-table tbody');
		const summaryTable = this.container?.querySelector('#financial-tab .summary-table');

		if (!tbody || !summaryTable) {
			console.warn('[QuickCompsView] Financial data table elements not found');
			return;
		}

		// Clear and populate company rows
		tbody.innerHTML = '';
		this.financialData.companies.forEach(company => {
			const row = document.createElement('tr');
			row.innerHTML = `
				<td><input type="checkbox"></td>
				<td>${company.name}</td>
				<td class="number">${company.price}</td>
				<td class="number">${company.shares}</td>
				<td class="number">${company.marketCap}</td>
				<td class="number">${company.debt}</td>
				<td class="number">${company.prefEquity}</td>
				<td class="number">${company.minorityInterest}</td>
				<td class="number">${company.enterpriseValue}</td>
				<td class="number">${company.bookValue}</td>
				<td>${company.filingDate}</td>
				<td class="number">${company.revenue}</td>
				<td class="number">${company.ebitda}</td>
				<td class="number">${company.ebit}</td>
				<td class="number">${company.eps}</td>
				<td class="number">${company.ntmRevenue}</td>
				<td class="number">${company.ntmEbitda}</td>
				<td class="number">${company.ntmEps}</td>
			`;
			tbody.appendChild(row);
		});

		// Update summary statistics
		const summary = this.financialData.summary;
		summaryTable.innerHTML = `
			<tr>
				<td>Summary Statistics</td>
				<td>Day Close Price Latest</td>
				<td>Shares Outstanding Latest</td>
				<td>Market Capitalization Latest</td>
				<td>LTM Net Debt</td>
				<td>LTM Total Pref. Equity</td>
				<td>LTM Minority Interest</td>
				<td>Total Enterprise Value Latest</td>
				<td>LTM Tangible Book Value/Share</td>
				<td>LTM Filing Date, Income Statement</td>
				<td>LTM Total Revenue</td>
				<td>LTM EBITDA</td>
				<td>LTM EBIT</td>
				<td>LTM Diluted EPS Excl. Extra Items</td>
				<td>NTM Revenue (ZapIQ)</td>
				<td>NTM EBITDA (ZapIQ)</td>
				<td>NTM EPS (ZapIQ)</td>
			</tr>
			<tr>
				<td>High</td>
				<td class="number">${summary.high.price}</td>
				<td class="number">${summary.high.shares}</td>
				<td class="number">${summary.high.marketCap}</td>
				<td class="number">${summary.high.debt}</td>
				<td class="number">${summary.high.prefEquity}</td>
				<td class="number">${summary.high.minorityInterest}</td>
				<td class="number">${summary.high.enterpriseValue}</td>
				<td class="number">${summary.high.bookValue}</td>
				<td>-</td>
				<td class="number">${summary.high.revenue}</td>
				<td class="number">${summary.high.ebitda}</td>
				<td class="number">${summary.high.ebit}</td>
				<td class="number">${summary.high.eps}</td>
				<td class="number">${summary.high.ntmRevenue}</td>
				<td class="number">${summary.high.ntmEbitda}</td>
				<td class="number">${summary.high.ntmEps}</td>
			</tr>
			<tr>
				<td>Low</td>
				<td class="number">${summary.low.price}</td>
				<td class="number">${summary.low.shares}</td>
				<td class="number">${summary.low.marketCap}</td>
				<td class="number">${summary.low.debt}</td>
				<td class="number">${summary.low.prefEquity}</td>
				<td class="number">${summary.low.minorityInterest}</td>
				<td class="number">${summary.low.enterpriseValue}</td>
				<td class="number">${summary.low.bookValue}</td>
				<td>-</td>
				<td class="number">${summary.low.revenue}</td>
				<td class="number">${summary.low.ebitda}</td>
				<td class="number">${summary.low.ebit}</td>
				<td class="number">${summary.low.eps}</td>
				<td class="number">${summary.low.ntmRevenue}</td>
				<td class="number">${summary.low.ntmEbitda}</td>
				<td class="number">${summary.low.ntmEps}</td>
			</tr>
			<tr>
				<td>Mean</td>
				<td class="number">${summary.mean.price}</td>
				<td class="number">${summary.mean.shares}</td>
				<td class="number">${summary.mean.marketCap}</td>
				<td class="number">${summary.mean.debt}</td>
				<td class="number">${summary.mean.prefEquity}</td>
				<td class="number">${summary.mean.minorityInterest}</td>
				<td class="number">${summary.mean.enterpriseValue}</td>
				<td class="number">${summary.mean.bookValue}</td>
				<td>-</td>
				<td class="number">${summary.mean.revenue}</td>
				<td class="number">${summary.mean.ebitda}</td>
				<td class="number">${summary.mean.ebit}</td>
				<td class="number">${summary.mean.eps}</td>
				<td class="number">${summary.mean.ntmRevenue}</td>
				<td class="number">${summary.mean.ntmEbitda}</td>
				<td class="number">${summary.mean.ntmEps}</td>
			</tr>
			<tr>
				<td>Median</td>
				<td class="number">${summary.median.price}</td>
				<td class="number">${summary.median.shares}</td>
				<td class="number">${summary.median.marketCap}</td>
				<td class="number">${summary.median.debt}</td>
				<td class="number">${summary.median.prefEquity}</td>
				<td class="number">${summary.median.minorityInterest}</td>
				<td class="number">${summary.median.enterpriseValue}</td>
				<td class="number">${summary.median.bookValue}</td>
				<td>-</td>
				<td class="number">${summary.median.revenue}</td>
				<td class="number">${summary.median.ebitda}</td>
				<td class="number">${summary.median.ebit}</td>
				<td class="number">${summary.median.eps}</td>
				<td class="number">${summary.median.ntmRevenue}</td>
				<td class="number">${summary.median.ntmEbitda}</td>
				<td class="number">${summary.median.ntmEps}</td>
			</tr>
		`;
	}

	/**
	 * Render the trading multiples table with data from JSON files
	 */
	private renderTradingMultiplesTable(): void {
		if (!this.tradingMultiples) {
			console.warn('[QuickCompsView] Trading multiples data not loaded yet');
			return;
		}

		const tbody = this.container?.querySelector('#trading-tab .comps-table.trading-multiples tbody');
		const summaryTable = this.container?.querySelector('#trading-tab .summary-table');

		if (!tbody || !summaryTable) {
			console.warn('[QuickCompsView] Trading multiples table elements not found');
			return;
		}

		// Clear and populate company rows
		tbody.innerHTML = '';
		this.tradingMultiples.companies.forEach(company => {
			const row = document.createElement('tr');
			row.innerHTML = `
				<td><input type="checkbox"></td>
				<td>${company.name}</td>
				<td class="number">${company.tevRevenues}</td>
				<td class="number">${company.tevEbitda}</td>
				<td class="number">${company.tevEbit}</td>
				<td class="number">${company.peDiluted}</td>
				<td class="number">${company.pTangBv}</td>
				<td class="number">${company.ntmTevRevenue}</td>
				<td class="number">${company.ntmTevEbitda}</td>
				<td class="number">${company.ntmPe}</td>
			`;
			tbody.appendChild(row);
		});

		// Update summary statistics
		const summary = this.tradingMultiples.summary;
		summaryTable.innerHTML = `
			<tr>
				<td>Summary Statistics</td>
				<td>TEV/Total Revenues LTM - Latest</td>
				<td>TEV/EBITDA LTM - Latest</td>
				<td>TEV/EBIT LTM - Latest</td>
				<td>P/Diluted EPS Before Extra LTM - Latest</td>
				<td>P/TangBV LTM - Latest</td>
				<td>NTM TEV/Forward Total Revenue (Capital IQ)</td>
				<td>NTM TEV/Forward EBITDA (Capital IQ)</td>
				<td>NTM Forward P/E (Capital IQ)</td>
			</tr>
			<tr>
				<td>High</td>
				<td class="number">${summary.high.tevRevenues}</td>
				<td class="number">${summary.high.tevEbitda}</td>
				<td class="number">${summary.high.tevEbit}</td>
				<td class="number">${summary.high.peDiluted}</td>
				<td class="number">${summary.high.pTangBv}</td>
				<td class="number">${summary.high.ntmTevRevenue}</td>
				<td class="number">${summary.high.ntmTevEbitda}</td>
				<td class="number">${summary.high.ntmPe}</td>
			</tr>
			<tr>
				<td>Low</td>
				<td class="number">${summary.low.tevRevenues}</td>
				<td class="number">${summary.low.tevEbitda}</td>
				<td class="number">${summary.low.tevEbit}</td>
				<td class="number">${summary.low.peDiluted}</td>
				<td class="number">${summary.low.pTangBv}</td>
				<td class="number">${summary.low.ntmTevRevenue}</td>
				<td class="number">${summary.low.ntmTevEbitda}</td>
				<td class="number">${summary.low.ntmPe}</td>
			</tr>
			<tr>
				<td>Mean</td>
				<td class="number">${summary.mean.tevRevenues}</td>
				<td class="number">${summary.mean.tevEbitda}</td>
				<td class="number">${summary.mean.tevEbit}</td>
				<td class="number">${summary.mean.peDiluted}</td>
				<td class="number">${summary.mean.pTangBv}</td>
				<td class="number">${summary.mean.ntmTevRevenue}</td>
				<td class="number">${summary.mean.ntmTevEbitda}</td>
				<td class="number">${summary.mean.ntmPe}</td>
			</tr>
			<tr>
				<td>Median</td>
				<td class="number">${summary.median.tevRevenues}</td>
				<td class="number">${summary.median.tevEbitda}</td>
				<td class="number">${summary.median.tevEbit}</td>
				<td class="number">${summary.median.peDiluted}</td>
				<td class="number">${summary.median.pTangBv}</td>
				<td class="number">${summary.median.ntmTevRevenue}</td>
				<td class="number">${summary.median.ntmTevEbitda}</td>
				<td class="number">${summary.median.ntmPe}</td>
			</tr>
		`;
	}

	/**
	 * Render the operating statistics table with data from JSON files
	 */
	private renderOperatingStatsTable(): void {
		if (!this.operatingStats) {
			console.warn('[QuickCompsView] Operating stats data not loaded yet');
			return;
		}

		const tbody = this.container?.querySelector('#operating-tab .comps-table.operating-stats tbody');
		const summaryTable = this.container?.querySelector('#operating-tab .summary-table');

		if (!tbody || !summaryTable) {
			console.warn('[QuickCompsView] Operating stats table elements not found');
			return;
		}

		// Clear and populate company rows
		tbody.innerHTML = '';
		this.operatingStats.companies.forEach(company => {
			const row = document.createElement('tr');
			row.innerHTML = `
				<td><input type="checkbox"></td>
				<td>${company.name}</td>
				<td class="number">${company.ltmGrossMargin}</td>
				<td class="number">${company.ltmEbitdaMargin}</td>
				<td class="number">${company.ltmEbitMargin}</td>
				<td class="number">${company.ltmNetIncomeMargin}</td>
				<td class="number">${company.ltmRevenueGrowth}</td>
				<td class="number">${company.ltmEbitdaGrowth}</td>
				<td class="number">${company.ltmEbitGrowth}</td>
				<td class="number">${company.ltmNetIncomeGrowth}</td>
				<td class="number">${company.ltmDebtCapital}</td>
				<td class="number">${company.ltmDebtEbitda}</td>
				<td class="number">${company.ntmEpsGrowth}</td>
				<td class="number">${company.beta5Year}</td>
			`;
			tbody.appendChild(row);
		});

		// Update summary statistics
		const summary = this.operatingStats.summary;
		summaryTable.innerHTML = `
			<tr>
				<td>Summary Statistics</td>
				<td>LTM Gross Margin %</td>
				<td>LTM EBITDA Margin %</td>
				<td>LTM EBIT Margin %</td>
				<td>LTM Net Income Margin %</td>
				<td>LTM Total Revenues, 1 Yr Growth %</td>
				<td>LTM EBITDA, 1 Yr Growth %</td>
				<td>LTM EBIT, 1 Yr Growth %</td>
				<td>LTM Net Income, 1 Yr Growth %</td>
				<td>LTM Total Debt/Capital %</td>
				<td>LTM Total Debt/EBITDA</td>
				<td>NTM LT EPS Growth Rate (Capital IQ)</td>
				<td>5 Year Beta</td>
			</tr>
			<tr>
				<td>High</td>
				<td class="number">${summary.high.ltmGrossMargin}</td>
				<td class="number">${summary.high.ltmEbitdaMargin}</td>
				<td class="number">${summary.high.ltmEbitMargin}</td>
				<td class="number">${summary.high.ltmNetIncomeMargin}</td>
				<td class="number">${summary.high.ltmRevenueGrowth}</td>
				<td class="number">${summary.high.ltmEbitdaGrowth}</td>
				<td class="number">${summary.high.ltmEbitGrowth}</td>
				<td class="number">${summary.high.ltmNetIncomeGrowth}</td>
				<td class="number">${summary.high.ltmDebtCapital}</td>
				<td class="number">${summary.high.ltmDebtEbitda}</td>
				<td class="number">${summary.high.ntmEpsGrowth}</td>
				<td class="number">${summary.high.beta5Year}</td>
			</tr>
			<tr>
				<td>Low</td>
				<td class="number">${summary.low.ltmGrossMargin}</td>
				<td class="number">${summary.low.ltmEbitdaMargin}</td>
				<td class="number">${summary.low.ltmEbitMargin}</td>
				<td class="number">${summary.low.ltmNetIncomeMargin}</td>
				<td class="number">${summary.low.ltmRevenueGrowth}</td>
				<td class="number">${summary.low.ltmEbitdaGrowth}</td>
				<td class="number">${summary.low.ltmEbitGrowth}</td>
				<td class="number">${summary.low.ltmNetIncomeGrowth}</td>
				<td class="number">${summary.low.ltmDebtCapital}</td>
				<td class="number">${summary.low.ltmDebtEbitda}</td>
				<td class="number">${summary.low.ntmEpsGrowth}</td>
				<td class="number">${summary.low.beta5Year}</td>
			</tr>
			<tr>
				<td>Mean</td>
				<td class="number">${summary.mean.ltmGrossMargin}</td>
				<td class="number">${summary.mean.ltmEbitdaMargin}</td>
				<td class="number">${summary.mean.ltmEbitMargin}</td>
				<td class="number">${summary.mean.ltmNetIncomeMargin}</td>
				<td class="number">${summary.mean.ltmRevenueGrowth}</td>
				<td class="number">${summary.mean.ltmEbitdaGrowth}</td>
				<td class="number">${summary.mean.ltmEbitGrowth}</td>
				<td class="number">${summary.mean.ltmNetIncomeGrowth}</td>
				<td class="number">${summary.mean.ltmDebtCapital}</td>
				<td class="number">${summary.mean.ltmDebtEbitda}</td>
				<td class="number">${summary.mean.ntmEpsGrowth}</td>
				<td class="number">${summary.mean.beta5Year}</td>
			</tr>
			<tr>
				<td>Median</td>
				<td class="number">${summary.median.ltmGrossMargin}</td>
				<td class="number">${summary.median.ltmEbitdaMargin}</td>
				<td class="number">${summary.median.ltmEbitMargin}</td>
				<td class="number">${summary.median.ltmNetIncomeMargin}</td>
				<td class="number">${summary.median.ltmRevenueGrowth}</td>
				<td class="number">${summary.median.ltmEbitdaGrowth}</td>
				<td class="number">${summary.median.ltmEbitGrowth}</td>
				<td class="number">${summary.median.ltmNetIncomeGrowth}</td>
				<td class="number">${summary.median.ltmDebtCapital}</td>
				<td class="number">${summary.median.ltmDebtEbitda}</td>
				<td class="number">${summary.median.ntmEpsGrowth}</td>
				<td class="number">${summary.median.beta5Year}</td>
			</tr>
		`;
	}

	/**
	 * Initialize date selector change handlers
	 */
	private initializeDateSelectors(): void {
		// Get all date selectors in the view (there are multiple, one per tab)
		const monthSelects = this.container?.querySelectorAll('.month-select');
		const yearSelects = this.container?.querySelectorAll('.year-select');
		const goButtons = this.container?.querySelectorAll('.date-go-button');

		if (!monthSelects || !yearSelects || !goButtons) {
			console.warn('[QuickCompsView] Date selectors not found');
			return;
		}

		// Handle Go button clicks
		goButtons.forEach(button => {
			this.addEventListener(button as HTMLElement, 'click', () => {
				void this.handleDateChange();
			});
		});
	}

	/**
	 * Handle date selector changes
	 */
	private async handleDateChange(): Promise<void> {
		// Get the current selected year from any year selector (they should all be synced)
		const yearSelect = this.container?.querySelector('.year-select') as HTMLSelectElement | null;

		if (!yearSelect) {
			return;
		}

		const newYear = yearSelect.value as DataYear;

		// Only reload if the year actually changed
		if (newYear !== this.currentDataYear) {
			this.currentDataYear = newYear;

			// Reload all year-dependent data for the new year
			try {
				[this.financialData, this.tradingMultiples, this.operatingStats] = await Promise.all([
					this.dataService.loadFinancialData(this.currentDataYear),
					this.dataService.loadTradingMultiples(this.currentDataYear),
					this.dataService.loadOperatingStats(this.currentDataYear)
				]);
			} catch (error) {
				// If 2025 data doesn't exist, fall back to 2024
				if (this.currentDataYear === '2025') {
					console.warn('[QuickCompsView] 2025 data not available, falling back to 2024');
					[this.financialData, this.tradingMultiples, this.operatingStats] = await Promise.all([
						this.dataService.loadFinancialData('2024'),
						this.dataService.loadTradingMultiples('2024'),
						this.dataService.loadOperatingStats('2024')
					]);
				} else {
					console.error('[QuickCompsView] Failed to reload data for new year:', error);
					return;
				}
			}

			// Re-render all the tables
			this.renderFinancialDataTable();
			this.renderTradingMultiplesTable();
			this.renderOperatingStatsTable();
		}
	}

	/**
	 * Initialize options dropdown functionality
	 */
	private initializeOptionsDropdown(): void {
		if (!this.companyDeletionHandler) {
			console.warn('[QuickCompsView] CompanyDeletionHandler not initialized');
			return;
		}

		// Create action callback for dropdown
		const handleAction: ActionCallback = (action: DropdownAction, _data?: unknown) => {
			if (action === 'chart') {
				// Handle chart action (matching original behavior)
				// Note: _data contains selected companies as string[]
			} else if (action === 'delete') {
				// Handle delete action
				if (!this.companyDeletionHandler) return;

				const deletedCompanies = this.companyDeletionHandler.deleteCompanies();

				// Update data objects if companies were deleted
				if (deletedCompanies.length > 0) {
					// Update financialData
					if (this.financialData && this.financialData.companies) {
						this.financialData.companies = this.financialData.companies.filter(
							company => !deletedCompanies.includes(company.name)
						);
					}

					// Update tradingMultiples
					if (this.tradingMultiples && this.tradingMultiples.companies) {
						this.tradingMultiples.companies = this.tradingMultiples.companies.filter(
							company => !deletedCompanies.includes(company.name)
						);
					}

					// Update operatingStats
					if (this.operatingStats && this.operatingStats.companies) {
						this.operatingStats.companies = this.operatingStats.companies.filter(
							company => !deletedCompanies.includes(company.name)
						);
					}

				}
			}
		};

		// Initialize OptionsDropdown with callback
		this.optionsDropdown = new OptionsDropdown(handleAction);
		this.optionsDropdown.initialize();
	}

	/**
	 * Escape HTML special characters to prevent XSS
	 */
	private escapeHtml(text: string): string {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
}
